/* ==========================================================================
   voxel-octopus.js — the build half of the procedurally animated octopus.

   Vendored verbatim apart from ESM exports. The POSE is not here: arms are
   baked in a PARAMETER space, not a pose — each arm voxel stores (which arm,
   how far along, where in the cross-section) and the vertex shader evaluates
   the curve every frame. OCTO_ARM_GLSL below is that evaluator, kept in this
   file so the parameterisation and the code that reads it cannot drift.

   ARMS ON THE GPU
   Each arm's centreline is a closed-form parametric curve, not an integrated
   skeleton. That matters: because the curve and its DERIVATIVE both have
   analytic forms, the vertex shader can evaluate the point, get the exact
   tangent, build a frame and place a voxel — all per vertex. So eight arms
   of a few thousand voxels animate in one draw call with nothing rebuilt on
   the CPU. An integrated chain would have to be stepped in order and could
   not be done per-vertex at all.

   The frame is carried from the arm's own base direction rather than from
   world up. A cross(up, tangent) frame degenerates the moment an arm curls
   to point straight down, which is exactly when a curling octopus needs it.
   ========================================================================== */
'use strict';

const TAU = Math.PI * 2;
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;
function sstep(e0, e1, x) { const t = clamp((x - e0) / (e1 - e0), 0, 1); return t * t * (3 - 2 * t); }

function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function makeNoise(seed) {
  const r = mulberry32(seed >>> 0), P = new Float32Array(1024);
  for (let i = 0; i < 1024; i++) P[i] = r();
  function h(x, y, z) {
    let n = (x * 374761393 + y * 668265263 + z * 1442695041) | 0;
    n = ((n ^ (n >> 13)) * 1274126177) | 0;
    return P[(n >>> 0) & 1023];
  }
  return function (x, y, z) {
    const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
    const xf = x - xi, yf = y - yi, zf = z - zi;
    const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf), w = zf * zf * (3 - 2 * zf);
    const a = lerp(h(xi, yi, zi), h(xi + 1, yi, zi), u), b = lerp(h(xi, yi + 1, zi), h(xi + 1, yi + 1, zi), u);
    const c = lerp(h(xi, yi, zi + 1), h(xi + 1, yi, zi + 1), u), d = lerp(h(xi, yi + 1, zi + 1), h(xi + 1, yi + 1, zi + 1), u);
    return lerp(lerp(a, b, v), lerp(c, d, v), w);
  };
}

const ARMS = 8;
/* voxel roles, which the shader uses to pick pigment */
const R_SKIN = 0, R_SUCKER = 1, R_EYE = 2, R_IRIS = 3, R_MANTLE = 4, R_LID = 5;

function buildOctopus(seed, vox, armLen, mantle) {
  const t0 = (typeof performance !== 'undefined' ? performance : Date).now();
  const rnd = mulberry32(seed >>> 0), nz = makeNoise(seed * 7919 + 11);

  const px = [], py = [], pz = [], arm = [], at = [], ax = [], az = [], role = [], shade = [];
  let nBody = 0, nArm = 0, nSuck = 0;

  /* ---- body: mantle sac plus head, as a voxel shell ---- */
  const MR = 0.335 * mantle, MH = 0.50 * mantle;       /* mantle radii            */
  const MY = 0.52 * mantle;                            /* mantle centre height    */
  const HR = 0.255 * mantle, HY = 0.12 * mantle;       /* head                    */
  const inside = (x, y, z) => {
    /* teardrop mantle: narrows toward the top, so it reads as a sac and not
       as an egg */
    const ty = (y - MY) / MH;
    const taper = 1 - 0.30 * sstep(0.0, 1.0, ty) - 0.10 * sstep(0.0, -1.0, ty);
    const mr = MR * taper;
    const m = (x * x + z * z) / (mr * mr) + ty * ty;
    if (m <= 1) return true;
    const dy = y - HY;
    return (x * x + dy * dy * 1.35 + z * z) <= HR * HR;
  };
  const lo = -Math.max(MR, HR) - vox * 2, hiY = MY + MH + vox * 2, loY = HY - HR - vox * 2;
  for (let y = loY; y <= hiY; y += vox)
    for (let x = lo; x <= -lo; x += vox)
      for (let z = lo; z <= -lo; z += vox) {
        if (!inside(x, y, z)) continue;
        /* shell only: keep a voxel if any 6-neighbour is outside */
        if (inside(x + vox, y, z) && inside(x - vox, y, z) && inside(x, y + vox, z) &&
          inside(x, y - vox, z) && inside(x, y, z + vox) && inside(x, y, z - vox)) continue;
        let r = R_MANTLE;
        /* Eyes. On a real octopus the eye reads as a DARK HORIZONTAL PUPIL with
           only a sliver of iris around it, seated in a dark lid — not a bright
           disc. A filled iris at this voxel count is just a coloured square. */
        const ey = HY + HR * 0.27;
        for (const s of [1, -1]) {
          const dx = x - s * HR * 0.84, dy = y - ey, dz = z;
          if (Math.abs(dx) > HR * 0.50) continue;
          const rr = Math.hypot(dy * 1.22, dz * 0.70);        /* wider than tall */
          if (rr > HR * 0.50) continue;
          /* The iris is GUARANTEED one voxel thick rather than left to fall out
             of a radius test. A thin ring only survives if the lattice happens to
             land inside it, so it filled the eye at one resolution and vanished
             at the next. Banding by height also matches the real animal, whose
             iris shows as arcs above and below a horizontal pupil. */
          const ph = HR * 0.16, ih = ph + Math.max(vox * 1.05, HR * 0.10);
          if (Math.abs(dy) <= ph && Math.abs(dz) < HR * 0.52) r = R_EYE;   /* slit pupil  */
          else if (Math.abs(dy) <= ih && Math.abs(dz) < HR * 0.58) r = R_IRIS; /* iris arcs */
          else r = R_LID;                                /* dark rim, seats the eye */
        }
        px.push(x); py.push(y); pz.push(z);
        arm.push(-1); at.push(0); ax.push(0); az.push(0); role.push(r);
        shade.push(0.86 + 0.28 * (nz(x * 9, y * 9, z * 9)));
        nBody++;
      }

  /* ---- arms: baked in parameter space ---- */
  const AR0 = 0.115 * mantle;                       /* arm radius at the crown */
  const CROWN = HY - HR * 0.55;
  const STEP = vox * 0.80;                          /* along-arm voxel spacing */
  for (let a = 0; a < ARMS; a++) {
    const steps = Math.max(8, Math.round(armLen / STEP));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      /* taper to a fine tip; the last stretch is a single voxel thread */
      const r = AR0 * Math.pow(1 - t, 0.72) * (1 - 0.12 * Math.sin(t * 9.0));
      if (r < vox * 0.62) {
        px.push(0); py.push(CROWN); pz.push(0);
        arm.push(a); at.push(t); ax.push(0); az.push(0); role.push(R_SKIN);
        shade.push(0.88 + 0.24 * nz(a * 3.1, t * 22, 0));
        nArm++; continue;
      }
      const ring = Math.max(4, Math.round(TAU * r / (vox * 0.88)));
      for (let k = 0; k < ring; k++) {
        const ang = k / ring * TAU;
        const ox = Math.cos(ang) * r, oz = Math.sin(ang) * r;
        /* suckers: two rows on the ventral face, which is -B in the arm's
           own frame, so they stay put as the arm curls */
        const vent = Math.sin(ang);
        const isSuck = vent < -0.35 && Math.abs(Math.cos(ang)) < 0.72 &&
          (i % 3 === 0) && t < 0.94 && r > vox * 1.1;
        px.push(0); py.push(CROWN); pz.push(0);
        arm.push(a); at.push(t); ax.push(ox); az.push(oz);
        role.push(isSuck ? R_SUCKER : R_SKIN);
        shade.push(0.88 + 0.24 * nz(a * 3.1 + ox * 7, t * 22, oz * 7));
        if (isSuck) nSuck++;
        nArm++;
      }
    }
  }

  const n = px.length;
  return {
    n, nBody, nArm, nSuck, vox, crown: CROWN, armLen,
    x: new Float32Array(px), y: new Float32Array(py), z: new Float32Array(pz),
    arm: new Float32Array(arm), t: new Float32Array(at),
    ox: new Float32Array(ax), oz: new Float32Array(az),
    role: new Float32Array(role), shade: new Float32Array(shade),
    ms: (typeof performance !== 'undefined' ? performance : Date).now() - t0
  };
}

/* ---- the arm curve, and its analytic derivative ----
   Every term is a polynomial or a sine, so the tangent is exact and cheap.
   That is the whole reason this can run per-vertex. */
const OCTO_ARM_GLSL = `
uniform float uTime,uLen,uBend,uWave,uPhase,uWaveFq,uSplay,uPitch,uCrown,uJet,uOpen,uCrawl,uGait,uBreath,uMantleY;
attribute float aArm, aT, aRole, aShade;
attribute vec2  aOff;

float h11(float n){ return fract(sin(n*127.1+311.7)*43758.5453); }

void armPoint(float a,float t,out vec3 P,out vec3 T){
  /* Per-arm gait. Hashing the arm index gives each limb its own rate and
     phase, so crawling stops being eight copies of one motion offset by a
     fixed angle. Two incommensurate oscillators per arm mean the set never
     falls back into step, and raising the result to a power makes the lift
     brief and the stance long — a limb that plants and hauls, then flicks
     forward, rather than one that waves symmetrically. */
  float r1 = h11(a*1.7+3.1), r2 = h11(a*2.3+7.7), r3 = h11(a*3.1+11.3);
  float gp = uTime*(0.85+0.70*r1)*uGait + r2*6.2831853;
  float raw = sin(gp)*0.68 + sin(gp*0.61 + r3*6.2831853)*0.32;
  float st  = pow(0.5+0.5*raw, 2.2);               /* 0 planted, 1 lifted   */
  float cw  = uCrawl;

  /* arms also swing in and out of the body line as they step */
  float th = a*0.7853981634 + cw*sin(gp*0.53 + r1*6.2831853)*0.20;
  float ph = a*2.3999632 + r2*6.2831853*cw;        /* per-arm wave phase    */
  /* Phase is INTEGRATED on the CPU, not uTime multiplied by a live speed.
     Scaling absolute time means any change to the speed jumps the phase by
     uTime*deltaSpeed — which grows without bound, so a behaviour switch a
     minute in sweeps whole wave cycles per frame and reads as vibration. */
  float w  = t*uWaveFq*6.2831853 - uPhase + ph;

  /* The arm is a circular arc: bend is an ANGLE per unit length, not a
     displacement, so the arc length stays exactly uLen at any curvature and
     the arm no longer shortens as it coils.

       phi(t) = pitch + A*t
       u(t)   = (L/A)(sin(phi) - sin(pitch))
       y(t)   = (L/A)(cos(pitch) - cos(phi))

     and the tangent is simply (cos phi, sin phi) — exact, which is what lets
     this run per-vertex. */
  float A  = uBend*(1.0 - uJet*0.65) - uJet*0.06 + uOpen*0.72
           + cw*st*(0.50 + 0.55*r3);
  float p0 = uPitch + uSplay - uJet*0.20 + uOpen*1.05
           + cw*(st*0.62 - 0.18)*(0.75 + 0.50*r3);
  /* A -> 0 is a straight arm, i.e. infinite radius; guard the division */
  float As = (A >= 0.0 ? 1.0 : -1.0) * max(abs(A), 0.02);
  float f1 = p0 + As*t;

  float u = (uLen/As)*(sin(f1) - sin(p0));
  float y = (uLen/As)*(cos(p0) - cos(f1)) ;
  float v = uLen*uWave*t*sin(w);

  float du = uLen*cos(f1);
  float dy = uLen*sin(f1);
  float dv = uLen*uWave*(sin(w) + t*cos(w)*uWaveFq*6.2831853);

  float c=cos(th), s=sin(th);
  P = vec3(u*c - v*s, y + uCrown, u*s + v*c);
  T = normalize(vec3(du*c - dv*s, dy, du*s + dv*c));
}
`;

export { buildOctopus, OCTO_ARM_GLSL, ARMS, R_SKIN, R_SUCKER, R_EYE, R_IRIS, R_MANTLE, R_LID };
export default { buildOctopus, OCTO_ARM_GLSL, ARMS };
